import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import { LanguageProvider, useLanguage } from "./context/LanguageContext";
import SEOHead from "./components/SEOHead";
import { initScrollAnimations } from "./hooks/useScrollAnimation";

// New design components
import NewTopBar from "./components/NewTopBar";
import NewHeader from "./components/NewHeader";
import NewHeroSection from "./components/NewHeroSection";
import ProofStrip from "./components/ProofStrip";
import PhotoBand from "./components/PhotoBand";
import PainSection from "./components/PainSection";
import DifferenceSection from "./components/DifferenceSection";
import SizeFitSection from "./components/SizeFitSection";
import NewServicesSection from "./components/NewServicesSection";
import NewProcessSection from "./components/NewProcessSection";
import PackageSection from "./components/PackageSection";
import NotForEveryoneSection from "./components/NotForEveryoneSection";
import BlogSection from "./components/BlogSection";
import NewFAQSection from "./components/NewFAQSection";
import NewFooter from "./components/NewFooter";
import ChatWidget from "./components/ChatWidget";
import CookieBanner from "./components/CookieBanner";
import GoogleAnalytics from "./components/GoogleAnalytics";
import AdminBlogPanel from "./components/AdminBlogPanel";

// Legal pages
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfService from "./components/TermsOfService";
import CookiePolicy from "./components/CookiePolicy";

// Investors page
import InvestorsPage from "./components/InvestorsPage";

// Legacy imports (for service pages that still exist)
import RacunovodstvoPage from "./pages/RacunovodstvoPage";
import UstanovitevPodjetjaPage from "./pages/UstanovitevPodjetjaPage";
import DelovnaDovoljenjaPage from "./pages/DelovnaDovoljenjaPage";
import PisarneNajemPage from "./pages/PisarneNajemPage";
import CompanyFormationPage from "./pages/CompanyFormationPage";
import AccountingServicesPage from "./pages/AccountingServicesPage";
import OfficeRentalPage from "./pages/OfficeRentalPage";

const businessJsonLd = {
  "@context": "https://schema.org",
  "@type": "AccountingService",
  "name": "General Ledger d.o.o.",
  "alternateName": ["General Ledger Slovenia", "GL Accounting Slovenia"],
  "url": "https://general-ledger.eu",
  "logo": {
    "@type": "ImageObject",
    "url": "https://general-ledger.eu/images/logo-white.png",
    "width": 200,
    "height": 60
  },
  "image": "https://general-ledger.eu/images/exterior.jpg",
  "description": "Company formation, accounting, VAT registration, work permits and tax advisory services in Slovenia, EU. English-speaking team helping foreign investors start businesses since 2003.",
  "slogan": "Your trusted partner for starting a business in Slovenia",
  "telephone": "+386 3 818 40 60",
  "email": "info@general-ledger.eu",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Celjska cesta 47",
    "addressLocality": "Rogatec",
    "postalCode": "3252",
    "addressRegion": "Štajerska",
    "addressCountry": "SI"
  },
  "geo": { 
    "@type": "GeoCoordinates", 
    "latitude": 46.2289, 
    "longitude": 15.6873 
  },
  "areaServed": [
    { "@type": "Country", "name": "Slovenia" },
    { "@type": "AdministrativeArea", "name": "European Union" }
  ],
  "serviceArea": {
    "@type": "GeoCircle",
    "geoMidpoint": { "@type": "GeoCoordinates", "latitude": 46.0569, "longitude": 14.5058 },
    "geoRadius": "50000"
  },
  "knowsLanguage": ["Slovenian", "English", "Serbo-Croatian", "Spanish"],
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Business Services in Slovenia",
    "itemListElement": [
      {
        "@type": "Offer", 
        "itemOffered": {
          "@type": "Service", 
          "name": "Company Formation in Slovenia",
          "description": "Register d.o.o. (LLC), sole proprietorship (s.p.), or branch office in Slovenia. Full service in 7-14 days."
        }
      },
      {
        "@type": "Offer", 
        "itemOffered": {
          "@type": "Service", 
          "name": "Accounting & VAT Services",
          "description": "Monthly bookkeeping, VAT returns, financial statements. Fixed monthly fee, no surprises."
        }
      },
      {
        "@type": "Offer", 
        "itemOffered": {
          "@type": "Service", 
          "name": "Work & Residence Permits",
          "description": "Single permit applications for foreign workers, renewals, A1 forms for posted workers."
        }
      },
      {
        "@type": "Offer", 
        "itemOffered": {
          "@type": "Service", 
          "name": "Tax Advisory",
          "description": "Tax optimization, transfer pricing, international tax planning for EU businesses."
        }
      },
      {
        "@type": "Offer", 
        "itemOffered": {
          "@type": "Service", 
          "name": "Payroll Services",
          "description": "Salary calculations, social contributions, employment contracts for Slovenian and foreign employees."
        }
      }
    ]
  },
  "openingHoursSpecification": [
    { 
      "@type": "OpeningHoursSpecification", 
      "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], 
      "opens": "08:00", 
      "closes": "16:00" 
    }
  ],
  "priceRange": "$$",
  "currenciesAccepted": "EUR",
  "paymentAccepted": "Bank Transfer",
  "foundingDate": "2003",
  "sameAs": [
    "https://www.facebook.com/GeneralLedgerRogatec"
  ]
};

const HomePage = () => {
  // Initialize scroll animations after component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      initScrollAnimations();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-[#F6F2EA]">
      <SEOHead
        title="General Ledger — računovodstvo, ustanovitev podjetij in delovna dovoljenja"
        description="Računovodski servis General Ledger d.o.o., Rogatec — ustanovitev podjetij, računovodstvo in DDV, obračun plač, delovna dovoljenja in davčno svetovanje. Od leta 2003."
        canonicalPath="/"
        lang="sl"
        alternateUrl="/en"
        jsonLd={businessJsonLd}
      />
      <NewTopBar />
      <NewHeader />
      <main>
        <NewHeroSection />
        <ProofStrip />
        <PhotoBand />
        <PainSection />
        <DifferenceSection />
        <SizeFitSection />
        <NewServicesSection />
        <NewProcessSection />
        <PackageSection />
        <NotForEveryoneSection />
        <BlogSection />
        <NewFAQSection />
      </main>
      <NewFooter />
      <ChatWidget />
      <CookieBanner />
    </div>
  );
};

const EnglishHomePage = () => {
  const { setLanguage } = useLanguage();
  
  // Set language to English when this page loads
  useEffect(() => {
    setLanguage('en');
  }, [setLanguage]);

  // Initialize scroll animations after component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      initScrollAnimations();
    }, 100);
    return () => clearTimeout(timer);
  }, []);
  
  return (
    <div className="min-h-screen bg-[#F6F2EA]">
      <SEOHead
        title="General Ledger — accounting, company formation and work permits in Slovenia"
        description="General Ledger d.o.o. accounting firm, Rogatec — company formation, accounting and VAT, payroll, work permits and tax advisory in Slovenia. Since 2003."
        canonicalPath="/en"
        lang="en"
        alternateUrl="/"
        jsonLd={{
          ...businessJsonLd,
          "description": "General Ledger d.o.o. accounting firm, Rogatec — company formation, accounting and VAT, payroll, work permits and tax advisory in Slovenia. Since 2003."
        }}
      />
      <NewTopBar />
      <NewHeader />
      <main>
        <NewHeroSection />
        <ProofStrip />
        <PhotoBand />
        <PainSection />
        <DifferenceSection />
        <SizeFitSection />
        <NewServicesSection />
        <NewProcessSection />
        <PackageSection />
        <NotForEveryoneSection />
        <BlogSection />
        <NewFAQSection />
      </main>
      <NewFooter />
      <ChatWidget />
      <CookieBanner />
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <LanguageProvider>
        <div className="App">
          <Toaster position="top-center" richColors />
          <BrowserRouter>
            <GoogleAnalytics />
            <Routes>
              {/* Main pages */}
              <Route path="/" element={<HomePage />} />
              <Route path="/en" element={<EnglishHomePage />} />
              
              {/* Privacy & Legal pages */}
              <Route path="/zasebnost" element={<PrivacyPolicy />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/pogoji" element={<TermsOfService />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/piskotki" element={<CookiePolicy />} />
              <Route path="/cookies" element={<CookiePolicy />} />
              
              {/* SL service pages - redirect to main page sections */}
              <Route path="/racunovodstvo" element={<Navigate to="/#services" replace />} />
              <Route path="/ustanovitev-podjetja" element={<Navigate to="/#services" replace />} />
              <Route path="/delovna-dovoljenja" element={<Navigate to="/#services" replace />} />
              <Route path="/pisarne-najem" element={<Navigate to="/#office" replace />} />
              
              {/* EN service pages - redirect to main page sections */}
              <Route path="/company-formation-slovenia" element={<Navigate to="/en#services" replace />} />
              <Route path="/accounting-services-slovenia" element={<Navigate to="/en#services" replace />} />
              <Route path="/office-rental-slovenia" element={<Navigate to="/en#office" replace />} />
              
              {/* Legacy redirects */}
              <Route path="/postavitev-podjetja" element={<Navigate to="/#services" replace />} />
              <Route path="/blog" element={<Navigate to="/" replace />} />
              <Route path="/blog/:id" element={<Navigate to="/" replace />} />
              
              {/* Investors landing page */}
              <Route path="/investors" element={<InvestorsPage />} />
              <Route path="/investitorji" element={<InvestorsPage />} />
              
              {/* Admin panel */}
              <Route path="/admin" element={<AdminBlogPanel />} />
              <Route path="/admin/blog" element={<AdminBlogPanel />} />
              
              {/* Catch all - redirect to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </div>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
